import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { LinkButton, LinkIconButton } from '@atlaskit/button/new';
import { Inline } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';

import type { PersonProfileData } from '../../types';
import { getIconForExternalMessageIntegration } from '../utils/externalMessageIntegrationIcons';
import { resolveExternalMessageFromIntegrations } from '../utils/thirdPartyIntegrationsByProduct';

const messages = defineMessages({
	viewProfile: {
		id: 'user-profile-card.action-bar.view-profile',
		defaultMessage: 'View profile',
		description: 'Button label to navigate to a user profile from the profile card',
	},
	viewOrgChartLabel: {
		id: 'user-profile-card.action-bar.view-org-chart-label',
		defaultMessage: 'View org chart for {name}',
		description: 'Accessible label for the org chart icon button on the profile card',
	},
	sendMessageLabel: {
		id: 'user-profile-card.action-bar.send-message-label',
		defaultMessage: 'Send message to {name}',
		description:
			'Accessible label for the send message button (e.g. Slack or Microsoft Teams) on the profile card',
	},
});

interface HeaderActionsProps {
	profileData: PersonProfileData;
	cloudId: string;
}

/**
 * Primary actions for the profile card header (org entry + view profile in Teams).
 */
export function HeaderActions({
	profileData,
	cloudId,
}: HeaderActionsProps): React.JSX.Element | null {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const { accountId, name, thirdPartyIntegrations, email } = profileData;
	const { href, target, onNavigate } = navigateToTeamsApp({
		type: 'USER',
		payload: { userId: accountId },
		cloudId: cloudId ?? '',
	});

	const externalMessage = useMemo(
		() => resolveExternalMessageFromIntegrations(thirdPartyIntegrations, email),
		[email, thirdPartyIntegrations],
	);

	const handleThirdPartyMessageClick = useCallback(() => {
		fireEvent('ui.userProfileCard.clicked.thirdPartyMessage', {});
	}, [fireEvent]);

	const handleViewProfileClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			fireEvent('ui.userProfileCard.clicked.viewProfile', {});
			onNavigate?.(event);
		},
		[fireEvent, onNavigate],
	);

	if (!accountId || !href) {
		return null;
	}

	return (
		<Inline space="space.100" alignBlock="center">
			{externalMessage && (
				<LinkIconButton
					href={externalMessage.dm.openDmUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleThirdPartyMessageClick}
					icon={getIconForExternalMessageIntegration(externalMessage.dm.integration)}
					label={intl.formatMessage(messages.sendMessageLabel, { name })}
				/>
			)}
			{accountId && href && (
				<LinkButton
					href={href}
					appearance="default"
					target={target}
					onClick={handleViewProfileClick}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
				>
					{intl.formatMessage(messages.viewProfile)}
				</LinkButton>
			)}
		</Inline>
	);
}
